import { useEffect, useMemo, useRef, useState } from "react";
import SearchSelect from "./SearchSelect";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Paginator } from "primereact/paginator";
import { MultiSelect } from "primereact/multiselect";
import styles from "./CalculatorSplit.module.css";
import agent from "../../agent";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { connect } from "react-redux";
import Form from "./form";
import { ORGANIZATION_LOAD } from "../../constants/actionTypes";
import "primeicons/primeicons.css";
import RouteMap from "./RouteMap";
import mapPreviewImage from "./map-preview.svg";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import { useShoulderCalculator } from "./useShoulderCalculator";
import {
  buildSegmentVectorDetails,
  getRouteVectorTotalPrice,
  sumSegmentVectorTotalPrice,
} from "./segmentVectorDetails";
import { normalizeCarrierOptions } from "./carrierOptions";

const mapStateToProps = state => ({
  ...state.orgs,
});

const mapDispatchToProps = dispatch => ({
  onLoad: (payload) => {
    dispatch({ type: ORGANIZATION_LOAD, payload });
  },
});

const parcelPickupOptions = [
  { code: 1, name_ru: "От двери" },
  { code: 2, name_ru: "От терминала" },
];
const parcelDeliveryOptions = [
  { code: 1, name_ru: "До двери" },
  { code: 2, name_ru: "До терминала" },
];

const ESPRZ = "prz";
const ESPRD = "prd";
const CUSTOM_PAGE_SIZE = 5;
const CALCULATOR_SPLIT_STORAGE_KEY = "calculator_split_form_v1";
const AIRPLANE_BADGE_SRC = `${process.env.PUBLIC_URL || ""}/airplane-icon.svg`;

const EMPTY_KATO = { kato: "" };
const EMPTY_PRODUCT_CATEGORY = { code: "", name_ru: "" };
const EMPTY_ORG = { apiKey: null, id: null, name: "" };
const createEmptyCustomRouteFilter = () => ({
  minDaysCnt: null,
  maxDaysCnt: null,
  minPrice: null,
  maxPrice: null,
  carriers: [],
});
const NOOP = () => {};
const AIR_EXPRESS_CODES = [
  "EEXPRESS",
  "P151",
  "AVIA",
  "SLQEXP",
  "MIXTRB",
  "MIXFAST",
  "CURAVIA",
  "CURAUTO",
  "CUR",
  "EMEXAVIA",
  "MIXEXP",
];
const MAP_SEGMENT_COLORS = ["#2563EB", "#F97316", "#16A34A", "#9333EA", "#DC2626", "#0891B2", "#CA8A04"];
const VECTOR_TYPE_META_BY_CODE = {
  VECTOR: {
    nameRu: "Линейная перевозка",
    iconClass: "pi pi-truck",
    tooltip: "Прямое перемещение груза между точками маршрута.",
  },
  CROSS_DOCKING: {
    nameRu: "Кросс-докинг",
    iconClass: "pi pi-share-alt",
    tooltip: "Перегрузка груза на промежуточном узле без длительного хранения.",
  },
  TERMINAL: {
    nameRu: "Терминал",
    iconClass: "pi pi-map-marker",
    tooltip: "Прием, сортировка, хранение или выдача груза на терминале.",
  },
};

const MIX_DEFINITIONS = [
  {
    key: "MIX_OPT",
    title: "Тариф Оптимальный",
    subtitle: "Оптимальный баланс цены и сроков по среднему маршруту",
    badge: "Баланс",
    emptyText: "По текущим данным нет оптимального маршрута, который одновременно подходит по сроку и цене.",
  },
  {
    key: "MIX_TRB",
    title: "Тариф Экспресс",
    subtitle: "Самый быстрый вариант по времени доставки",
    badge: "Экспресс",
    emptyText: "Сервис расчета не вернул экспресс-маршрут для этих параметров.",
  },
  {
    key: "MIX_CHEAP",
    title: "Тариф Экономичный",
    subtitle: "Самый бюджетный вариант по итоговой стоимости",
    badge: "Эконом",
    emptyText: "Сервис расчета не вернул экономичный маршрут для этих параметров.",
  },
  {
    key: "MIX_FTL",
    title: "Тариф FTL",
    subtitle: "Выделенный транспорт под полный грузовой маршрут",
    badge: "FTL",
    emptyText: "FTL-тариф не вернулся в ответе сервиса для текущих параметров.",
  },
];

const toSafeNumber = (value) => Number(value) || 0;
const toNullableNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveDayRange = (source) => {
  const day = toNullableNumber(source?.daysCnt);
  const minDay = toNullableNumber(source?.minDay);
  const maxDay = toNullableNumber(source?.maxDay);
  const min = minDay ?? day ?? maxDay;
  const max = maxDay ?? day ?? minDay;

  if (min === null && max === null) {
    return { min: null, max: null };
  }

  return {
    min: min ?? max,
    max: max ?? min,
  };
};

const sumRouteDayRange = (route) => {
  const vectors = Array.isArray(route?.vectors) ? route.vectors : [];
  if (!vectors.length) {
    return resolveDayRange(route);
  }

  let sumMin = 0;
  let sumMax = 0;
  let hasAnyDays = false;
  vectors.forEach((vector) => {
    const range = resolveDayRange(vector);
    if (range.min === null && range.max === null) {
      return;
    }
    hasAnyDays = true;
    sumMin += toSafeNumber(range.min);
    sumMax += toSafeNumber(range.max);
  });

  return hasAnyDays ? { min: sumMin, max: sumMax } : resolveDayRange(route);
};

const formatDayRangeOrDash = (minDay, maxDay) => {
  const min = toNullableNumber(minDay);
  const max = toNullableNumber(maxDay);
  if (min === null && max === null) {
    return "—";
  }
  const normalizedMin = min ?? max;
  const normalizedMax = max ?? min;
  return `${toSafeNumber(normalizedMin)} - ${toSafeNumber(normalizedMax)} дн`;
};

const getRouteDaysLabel = (route) => {
  const range = sumRouteDayRange(route);
  return formatDayRangeOrDash(range.min, range.max);
};

const getDistanceKm = (route) => {
  if (!route?.vectors?.length) {
    return 0;
  }
  return Math.round(route.vectors.reduce((sum, v) => sum + toSafeNumber(v.distance), 0) / 1000);
};

const getRoutePath = (route) => route?.paths?.map(p => p.name).join(" → ") || "-";
const getVectorTypeMeta = (vectorType) => {
  const normalizedCode = String(vectorType || "").trim().toUpperCase();
  return VECTOR_TYPE_META_BY_CODE[normalizedCode] || {
    nameRu: "Тип не указан",
    iconClass: "pi pi-question-circle",
    tooltip: "Тип сегмента не передан в данных маршрута.",
  };
};

const getVectorCarrierLabel = (vector) => {
  const carrierType = String(vector?.carrierType || "").trim();
  const psName = String(vector?.psName || "").trim();
  if (carrierType && psName) {
    return normalizeRouteToken(carrierType) === normalizeRouteToken(psName)
      ? carrierType
      : `${carrierType} (${psName})`;
  }
  return carrierType || psName || "-";
};

const getRouteCarriers = (route) => route?.vectors?.map((vector) => getVectorCarrierLabel(vector)).join(" + ") || "-";

const formatPrice = (price) => `${toSafeNumber(price).toLocaleString("ru-RU")} тг`;
const formatMoneyOrDash = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return `${toSafeNumber(value).toLocaleString("ru-RU")} тг`;
};
const hasMoneyValue = (value) => value !== null && value !== undefined && value !== "";
const getLocationFullName = (location) => location?.nameRu || location?.name || "—";
const getVectorKatoCode = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    return String(value?.kato || value?.code || "").trim();
  }
  return "";
};

const normalizeRouteToken = (value) => String(value || "")
  .toUpperCase()
  .replace(/[^A-Z0-9]+/g, "");
const AIR_EXPRESS_PSNAME_SET = new Set(AIR_EXPRESS_CODES.map((code) => normalizeRouteToken(code)));
const isAirExpressPsName = (psName) => AIR_EXPRESS_PSNAME_SET.has(normalizeRouteToken(psName));

const hasAirExpressToken = (value) => {
  const normalized = normalizeRouteToken(value);
  if (!normalized) {
    return false;
  }

  return AIR_EXPRESS_CODES.some((code) => {
    if (code.length <= 3) {
      return normalized === code;
    }
    return normalized === code || normalized.includes(code);
  });
};

const isAirExpressRoute = (route) => {
  const candidates = [
    route?.psName,
    route?.serviceType,
    route?.tariff,
    route?.tariffName,
    route?.name,
  ];

  (route?.vectors || []).forEach((vector) => {
    candidates.push(vector?.psName);
    candidates.push(vector?.carrierType);
    candidates.push(vector?.serviceType);
    candidates.push(vector?.vectorType);
  });

  return candidates.some(hasAirExpressToken);
};

const getRouteIdentityKey = (route) => {
  if (!route || typeof route !== "object") {
    return "";
  }

  const primaryId = route.num ?? route.id ?? route.routeId ?? route.pathId ?? route.hashCode;
  if (primaryId !== null && primaryId !== undefined && primaryId !== "") {
    return `id:${String(primaryId)}`;
  }

  const firstPath = route?.paths?.[0]?.kato || route?.paths?.[0]?.name || "";
  const lastPath = route?.paths?.[route?.paths?.length - 1]?.kato || route?.paths?.[route?.paths?.length - 1]?.name || "";
  return `route:${firstPath}|${lastPath}|${toSafeNumber(route?.price)}|${toSafeNumber(route?.daysCnt)}|${route?.vectors?.length || 0}`;
};

const buildMixGroups = (routes, preferredHighlights = null) => {
  const hasPreferredHighlights = Boolean(preferredHighlights)
    && MIX_DEFINITIONS.some((definition) => !!preferredHighlights[definition.key]);

  if (hasPreferredHighlights) {
    const highlights = MIX_DEFINITIONS.map((definition) => ({
      ...definition,
      route: preferredHighlights[definition.key] || null,
    }));

    if (routes.length <= 3) {
      return { highlights, customRoutes: [] };
    }

    const selectedRouteKeys = new Set(
      highlights
        .filter((item) => !!item.route)
        .map((item) => getRouteIdentityKey(item.route))
    );
    const customRoutes = routes.filter((route) => !selectedRouteKeys.has(getRouteIdentityKey(route)));
    return { highlights, customRoutes };
  }

  if (!routes.length) {
    return {
      highlights: MIX_DEFINITIONS.map((definition) => ({
        ...definition,
        route: null,
      })),
      customRoutes: [],
    };
  }

  const usedRouteKeys = new Set();
  const pickDistinct = (candidates) => {
    const unique = candidates.find((route) => !usedRouteKeys.has(getRouteIdentityKey(route)));
    return unique || candidates[0];
  };

  const cheapCandidates = [...routes].sort((a, b) => {
    const priceDiff = toSafeNumber(a.price) - toSafeNumber(b.price);
    if (priceDiff !== 0) {
      return priceDiff;
    }
    return toSafeNumber(a.daysCnt) - toSafeNumber(b.daysCnt);
  });

  const fastCandidates = [...routes].sort((a, b) => {
    const daysDiff = toSafeNumber(a.daysCnt) - toSafeNumber(b.daysCnt);
    if (daysDiff !== 0) {
      return daysDiff;
    }
    return toSafeNumber(a.price) - toSafeNumber(b.price);
  });

  const cheap = pickDistinct(cheapCandidates);
  if (cheap) {
    usedRouteKeys.add(getRouteIdentityKey(cheap));
  }

  const fast = pickDistinct(fastCandidates);
  if (fast) {
    usedRouteKeys.add(getRouteIdentityKey(fast));
  }

  const priceFloor = Math.min(toSafeNumber(cheap?.price), toSafeNumber(fast?.price));
  const priceCeil = Math.max(toSafeNumber(cheap?.price), toSafeNumber(fast?.price));
  const daysFloor = Math.min(toSafeNumber(cheap?.daysCnt), toSafeNumber(fast?.daysCnt));
  const daysCeil = Math.max(toSafeNumber(cheap?.daysCnt), toSafeNumber(fast?.daysCnt));
  const priceRange = Math.max(priceCeil - priceFloor, 1);
  const daysRange = Math.max(daysCeil - daysFloor, 1);
  const targetPrice = (priceFloor + priceCeil) / 2;
  const targetDays = (daysFloor + daysCeil) / 2;

  const routeBetweenByPriceAndDays = (route) => {
    const price = toSafeNumber(route?.price);
    const days = toSafeNumber(route?.daysCnt);
    return price > priceFloor && price < priceCeil && days > daysFloor && days < daysCeil;
  };

  const optimalScore = (route) => {
    const price = toSafeNumber(route?.price);
    const days = toSafeNumber(route?.daysCnt);

    const priceOutsidePenalty = price < priceFloor
      ? (priceFloor - price) / priceRange
      : price > priceCeil
        ? (price - priceCeil) / priceRange
        : 0;
    const daysOutsidePenalty = days < daysFloor
      ? (daysFloor - days) / daysRange
      : days > daysCeil
        ? (days - daysCeil) / daysRange
        : 0;

    const midpointPenalty = Math.abs(price - targetPrice) / priceRange
      + Math.abs(days - targetDays) / daysRange;

    // Strongly prefer routes inside the economical/express band, then closest to midpoint.
    return (priceOutsidePenalty + daysOutsidePenalty) * 6 + midpointPenalty;
  };

  const strictOptimalCandidates = routes.filter(routeBetweenByPriceAndDays);
  const optimalCandidates = (strictOptimalCandidates.length ? strictOptimalCandidates : [...routes])
    .sort((a, b) => optimalScore(a) - optimalScore(b));

  let optimal = pickDistinct(optimalCandidates);
  if (optimal && fast && toSafeNumber(optimal.price) > toSafeNumber(fast.price)) {
    optimal = null;
  }
  if (optimal) {
    usedRouteKeys.add(getRouteIdentityKey(optimal));
  }

  const byKey = {
    MIX_OPT: optimal,
    MIX_CHEAP: cheap,
    MIX_TRB: fast,
  };

  const highlights = MIX_DEFINITIONS.map(definition => ({
    ...definition,
    route: byKey[definition.key],
  }));

  const selectedRouteKeys = new Set(highlights.filter((item) => !!item.route).map((item) => getRouteIdentityKey(item.route)));
  const customRoutes = routes.filter((route) => !selectedRouteKeys.has(getRouteIdentityKey(route)));

  return { highlights, customRoutes };
};

const mergeUniqueRoutes = (...routeCollections) => {
  const uniqueRoutes = new Map();

  routeCollections.forEach((routes) => {
    if (!Array.isArray(routes)) {
      return;
    }
    routes.forEach((route) => {
      const key = getRouteIdentityKey(route);
      if (!key) {
        return;
      }
      if (!uniqueRoutes.has(key)) {
        uniqueRoutes.set(key, route);
      }
    });
  });

  return Array.from(uniqueRoutes.values());
};

const buildOptimalRouteFromAdditional = (mainRoutes, additionalRoutes) => {
  const combinedRoutes = mergeUniqueRoutes(mainRoutes, additionalRoutes);
  if (!combinedRoutes.length) {
    return null;
  }

  const combinedMix = buildMixGroups(combinedRoutes);
  const optimalFromCombined = combinedMix.highlights.find((highlight) => highlight.key === "MIX_OPT")?.route;
  if (optimalFromCombined) {
    return optimalFromCombined;
  }

  return additionalRoutes?.[0] || null;
};

const CalculatorSplit = (props) => {
  const [from, setFrom] = useState(EMPTY_KATO);
  const [to, setTo] = useState(EMPTY_KATO);
  const [cargoValue, setCargoValue] = useState(null);
  const [numPack, setNumPack] = useState(null);
  const [weight, setWeight] = useState(null);
  const [productName, setProductName] = useState("");
  const [productCategory, setProductCategory] = useState(EMPTY_PRODUCT_CATEGORY);
  const [parcelPickup, setParcelPickup] = useState({});
  const [parcelDelivery, setParcelDelivery] = useState({});
  const [paidWeight, setPaidWeight] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(EMPTY_ORG);
  const [showMessage, setShowMessage] = useState(false);
  const [extraServices, setExtraServices] = useState([]);

  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [productCategoryOptions, setProductCategoryOptions] = useState([]);
  const [customCarrierOptions, setCustomCarrierOptions] = useState([]);
  const [isCustomCarrierOptionsLoading, setIsCustomCarrierOptionsLoading] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [searchMarkers, setSearchMarkers] = useState([]);
  const [storageHydrated, setStorageHydrated] = useState(false);
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState(null);
  const [isSegmentSidebarVisible, setIsSegmentSidebarVisible] = useState(true);
  const [segmentSidebarOffset, setSegmentSidebarOffset] = useState({ x: 0, y: 0 });
  const [isSegmentSidebarDragging, setIsSegmentSidebarDragging] = useState(false);
  const [formContainerOffset, setFormContainerOffset] = useState({ x: 0, y: 0 });
  const [isCollapsedTriggerDragging, setIsCollapsedTriggerDragging] = useState(false);
  const [isFloatingPanelDragging, setIsFloatingPanelDragging] = useState(false);
  const [customRouteFilter, setCustomRouteFilter] = useState(createEmptyCustomRouteFilter);

  const tableRef = useRef(null);
  const heroSectionRef = useRef(null);
  const collapsedTriggerRef = useRef(null);
  const floatingPanelRef = useRef(null);
  const sidebarDragRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0 });
  const collapsedTriggerDragRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  const floatingPanelDragRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const scrollToResultsSection = () => {
    if (typeof window === "undefined") {
      return;
    }
    window.setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const {
    result,
    loading,
    selectedRow,
    setSelectedRow,
    selectedRoute,
    setSelectedRoute,
    calculationHashCodeRequest,
    calculationBuildPathRequestId,
    calculationSelectedPathId,
    customFirst,
    setCustomFirst,
    hasCalculated,
    calcError,
    preferredHighlights,
    isCustomPolling,
    isAdditionalPolling,
    customPollingError,
    additionalRoutes,
    additionalPagination,
    debugPayload,
    isDebugCopied,
    setIsDebugCopied,
    loadAdditionalRoutesPage,
    sendCalculatorForm,
    resetCalculationState,
    copyDebugJson,
  } = useShoulderCalculator({
    fromKato: from.kato,
    toKato: to.kato,
    weight,
    cargoValue,
    numPack,
    paidWeight,
    productCategoryCode: productCategory.code,
    productName,
    parcelPickupCode: parcelPickup.code,
    parcelDeliveryCode: parcelDelivery.code,
    extraServices,
    orgId: selectedOrg.id,
    orgApiKey: selectedOrg?.apiKey,
    setFormCollapsed: setIsFormCollapsed,
    onAfterCalculation: scrollToResultsSection,
  });

  useBodyScrollLock(loading);
  const debugJson = useMemo(
    () => (debugPayload ? JSON.stringify(debugPayload, null, 2) : ""),
    [debugPayload]
  );

  const requiredFields = [
    from.kato,
    to.kato,
    cargoValue,
    numPack,
    weight,
    productName,
    productCategory.code,
    paidWeight,
    parcelPickup.code,
    parcelDelivery.code,
    selectedOrg.id,
  ];

  const routeGroups = useMemo(() => ({
    ...buildMixGroups(result, preferredHighlights),
    additionalRoutes,
  }), [additionalRoutes, preferredHighlights, result]);

  const highlightedRoutes = useMemo(() => {
    const hasOptimal = Boolean(routeGroups.highlights.find((item) => item.key === "MIX_OPT")?.route);
    if (hasOptimal || isAdditionalPolling || !routeGroups.additionalRoutes.length) {
      return routeGroups.highlights;
    }

    const optimalFromAdditional = buildOptimalRouteFromAdditional(result, routeGroups.additionalRoutes);
    if (!optimalFromAdditional) {
      return routeGroups.highlights;
    }

    return routeGroups.highlights.map((item) => (
      item.key === "MIX_OPT"
        ? { ...item, route: optimalFromAdditional }
        : item
    ));
  }, [isAdditionalPolling, result, routeGroups.additionalRoutes.length, routeGroups.highlights, routeGroups.additionalRoutes, result.length]);

  const isAnyCustomPolling = isCustomPolling || isAdditionalPolling;
  const hasAdditionalPagination = additionalPagination.totalElements > 0 || routeGroups.additionalRoutes.length > 0;
  const customPageItems = useMemo(
    () => routeGroups.customRoutes.slice(customFirst, customFirst + CUSTOM_PAGE_SIZE),
    [routeGroups.customRoutes, customFirst]
  );
  const visibleCustomRoutes = hasAdditionalPagination ? routeGroups.additionalRoutes : customPageItems;
  const customTotalRecords = hasAdditionalPagination ? additionalPagination.totalElements : routeGroups.customRoutes.length;
  const customPaginatorFirst = hasAdditionalPagination
    ? additionalPagination.pageNumber * additionalPagination.pageSize
    : customFirst;
  const customPaginatorRows = hasAdditionalPagination ? additionalPagination.pageSize : CUSTOM_PAGE_SIZE;

  const shouldShowResultsSection = hasCalculated && !calcError
    && (result.length > 0 || isAnyCustomPolling || routeGroups.highlights.length > 0);

  const mapSegments = useMemo(() => {
    const paths = selectedRow?.paths || [];
    const vectors = selectedRow?.vectors || [];
    if (paths.length < 2) {
      return [];
    }

    return paths.slice(0, -1).map((fromPath, index) => {
      const toPath = paths[index + 1] || {};
      const vector = vectors[index] || {};
      const psName = vector?.psName || vector?.carrierType || vector?.serviceType || "";
      const isExpress = isAirExpressPsName(psName);
      const dayRange = resolveDayRange(vector);
      const vectorTypeMeta = getVectorTypeMeta(vector?.vectorType);

      return {
        index,
        color: MAP_SEGMENT_COLORS[index % MAP_SEGMENT_COLORS.length],
        fromName: fromPath?.name || `Точка ${index + 1}`,
        toName: toPath?.name || `Точка ${index + 2}`,
        fromKato: getVectorKatoCode(vector?.fromKato ?? vector?.katoFrom ?? vector?.from),
        toKato: getVectorKatoCode(vector?.toKato ?? vector?.katoTo ?? vector?.to),
        carrierLabel: getVectorCarrierLabel(vector),
        mode: isExpress ? "air" : "car",
        distanceKm: Math.round(toSafeNumber(vector?.distance) / 1000),
        minDay: dayRange.min,
        maxDay: dayRange.max,
        price: vector?.price,
        prdPrice: vector?.prdPrice,
        przPrice: vector?.przPrice,
        crossDockingPrice: vector?.crossDockingPrice,
        details: buildSegmentVectorDetails(vector),
        vectorTypeNameRu: vectorTypeMeta.nameRu,
        vectorTypeIconClass: vectorTypeMeta.iconClass,
        vectorTypeTooltip: vectorTypeMeta.tooltip,
      };
    });
  }, [selectedRow]);
  const segmentTotalPrice = useMemo(
    () => sumSegmentVectorTotalPrice(selectedRow?.vectors || []),
    [selectedRow]
  );

  useEffect(() => {
    props.onLoad(agent.Organizations.all());

    if (typeof window === "undefined") {
      setStorageHydrated(true);
      return;
    }

    try {
      const rawValue = window.localStorage.getItem(CALCULATOR_SPLIT_STORAGE_KEY);
      if (!rawValue) {
        return;
      }

      const parsedValue = JSON.parse(rawValue);
      if (!parsedValue || typeof parsedValue !== "object") {
        return;
      }

      if (parsedValue.from && parsedValue.from.kato) {
        setFrom(parsedValue.from);
      }

      if (parsedValue.to && parsedValue.to.kato) {
        setTo(parsedValue.to);
      }

      if (parsedValue.productCategory && parsedValue.productCategory.code) {
        setProductCategory(parsedValue.productCategory);
      }

      if (parsedValue.parcelPickup && parsedValue.parcelPickup.code) {
        setParcelPickup(parsedValue.parcelPickup);
      }

      if (parsedValue.parcelDelivery && parsedValue.parcelDelivery.code) {
        setParcelDelivery(parsedValue.parcelDelivery);
      }

      if (parsedValue.selectedOrg && parsedValue.selectedOrg.id) {
        setSelectedOrg(parsedValue.selectedOrg);
      }

      setCargoValue(parsedValue.cargoValue ?? null);
      setNumPack(parsedValue.numPack ?? null);
      setWeight(parsedValue.weight ?? null);
      setPaidWeight(parsedValue.paidWeight ?? null);
      setProductName(parsedValue.productName ?? "");
      setExtraServices(Array.isArray(parsedValue.extraServices) ? parsedValue.extraServices : []);
    } catch (error) {
      console.error("Не удалось восстановить данные калькулятора", error);
    } finally {
      setStorageHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!storageHydrated || typeof window === "undefined") {
      return;
    }

    const payload = {
      from,
      to,
      cargoValue,
      numPack,
      weight,
      productName,
      productCategory,
      parcelPickup,
      parcelDelivery,
      paidWeight,
      selectedOrg,
      extraServices,
    };

    window.localStorage.setItem(CALCULATOR_SPLIT_STORAGE_KEY, JSON.stringify(payload));
  }, [
    storageHydrated,
    from,
    to,
    cargoValue,
    numPack,
    weight,
    productName,
    productCategory,
    parcelPickup,
    parcelDelivery,
    paidWeight,
    selectedOrg,
    extraServices,
  ]);

  useEffect(() => {
    if (!hasAdditionalPagination) {
      setCustomFirst(0);
    }
  }, [hasAdditionalPagination, routeGroups.customRoutes.length, setCustomFirst]);

  useEffect(() => {
    setHoveredSegmentIndex(null);
  }, [selectedRow]);

  useEffect(() => {
    if (!isDebugCopied) {
      return undefined;
    }

    const timeoutId = setTimeout(() => setIsDebugCopied(false), 1800);
    return () => clearTimeout(timeoutId);
  }, [isDebugCopied]);

  useEffect(() => {
    if (!isSegmentSidebarDragging || typeof window === "undefined") {
      return undefined;
    }

    const onMouseMove = (event) => {
      const dx = event.clientX - sidebarDragRef.current.startX;
      const dy = event.clientY - sidebarDragRef.current.startY;
      setSegmentSidebarOffset({
        x: sidebarDragRef.current.originX + dx,
        y: sidebarDragRef.current.originY + dy,
      });
    };

    const onMouseUp = () => {
      setIsSegmentSidebarDragging(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isSegmentSidebarDragging]);

  useEffect(() => {
    if (!isCollapsedTriggerDragging || typeof window === "undefined") {
      return undefined;
    }

    const onPointerMove = (event) => {
      if (event.pointerId !== collapsedTriggerDragRef.current.pointerId) {
        return;
      }

      const triggerElement = collapsedTriggerRef.current;
      const heroElement = heroSectionRef.current;
      if (!triggerElement || !heroElement) {
        return;
      }

      const dx = event.clientX - collapsedTriggerDragRef.current.startX;
      const dy = event.clientY - collapsedTriggerDragRef.current.startY;
      const nextX = collapsedTriggerDragRef.current.originX + dx;
      const nextY = collapsedTriggerDragRef.current.originY + dy;

      const computedStyle = window.getComputedStyle(triggerElement);
      const baseLeft = Number.parseFloat(computedStyle.left) || 0;
      const baseTop = Number.parseFloat(computedStyle.top) || 0;
      const maxX = heroElement.clientWidth - triggerElement.offsetWidth - baseLeft;
      const maxY = heroElement.clientHeight - triggerElement.offsetHeight - baseTop;
      const clampedX = Math.max(-baseLeft, Math.min(nextX, maxX));
      const clampedY = Math.max(-baseTop, Math.min(nextY, maxY));
      setFormContainerOffset({ x: clampedX, y: clampedY });
    };

    const onPointerEnd = (event) => {
      if (event.pointerId !== collapsedTriggerDragRef.current.pointerId) {
        return;
      }
      setIsCollapsedTriggerDragging(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [isCollapsedTriggerDragging]);

  useEffect(() => {
    if (!isFormCollapsed || !isMapVisible || typeof window === "undefined") {
      return undefined;
    }

    const keepCollapsedTriggerInBounds = () => {
      const triggerElement = collapsedTriggerRef.current;
      const heroElement = heroSectionRef.current;
      if (!triggerElement || !heroElement) {
        return;
      }

      const computedStyle = window.getComputedStyle(triggerElement);
      const baseLeft = Number.parseFloat(computedStyle.left) || 0;
      const baseTop = Number.parseFloat(computedStyle.top) || 0;
      const maxX = heroElement.clientWidth - triggerElement.offsetWidth - baseLeft;
      const maxY = heroElement.clientHeight - triggerElement.offsetHeight - baseTop;

      setFormContainerOffset((prev) => ({
        x: Math.max(-baseLeft, Math.min(prev.x, maxX)),
        y: Math.max(-baseTop, Math.min(prev.y, maxY)),
      }));
    };

    keepCollapsedTriggerInBounds();
    window.addEventListener("resize", keepCollapsedTriggerInBounds);
    return () => {
      window.removeEventListener("resize", keepCollapsedTriggerInBounds);
    };
  }, [isFormCollapsed, isMapVisible]);

  useEffect(() => {
    if (!isFloatingPanelDragging || typeof window === "undefined") {
      return undefined;
    }

    const onPointerMove = (event) => {
      if (event.pointerId !== floatingPanelDragRef.current.pointerId) {
        return;
      }

      const panelElement = floatingPanelRef.current;
      const heroElement = heroSectionRef.current;
      if (!panelElement || !heroElement) {
        return;
      }

      const dx = event.clientX - floatingPanelDragRef.current.startX;
      const dy = event.clientY - floatingPanelDragRef.current.startY;
      const nextX = floatingPanelDragRef.current.originX + dx;
      const nextY = floatingPanelDragRef.current.originY + dy;

      const computedStyle = window.getComputedStyle(panelElement);
      const baseLeft = Number.parseFloat(computedStyle.left) || 0;
      const baseTop = Number.parseFloat(computedStyle.top) || 0;
      const maxX = heroElement.clientWidth - panelElement.offsetWidth - baseLeft;
      const maxY = heroElement.clientHeight - panelElement.offsetHeight - baseTop;
      const clampedX = Math.max(-baseLeft, Math.min(nextX, maxX));
      const clampedY = Math.max(-baseTop, Math.min(nextY, maxY));
      setFormContainerOffset({ x: clampedX, y: clampedY });
    };

    const onPointerEnd = (event) => {
      if (event.pointerId !== floatingPanelDragRef.current.pointerId) {
        return;
      }
      setIsFloatingPanelDragging(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [isFloatingPanelDragging]);

  useEffect(() => {
    if (isFormCollapsed || !isMapVisible || typeof window === "undefined") {
      return undefined;
    }

    const keepFloatingPanelInBounds = () => {
      const panelElement = floatingPanelRef.current;
      const heroElement = heroSectionRef.current;
      if (!panelElement || !heroElement) {
        return;
      }

      const computedStyle = window.getComputedStyle(panelElement);
      const baseLeft = Number.parseFloat(computedStyle.left) || 0;
      const baseTop = Number.parseFloat(computedStyle.top) || 0;
      const maxX = heroElement.clientWidth - panelElement.offsetWidth - baseLeft;
      const maxY = heroElement.clientHeight - panelElement.offsetHeight - baseTop;

      setFormContainerOffset((prev) => ({
        x: Math.max(-baseLeft, Math.min(prev.x, maxX)),
        y: Math.max(-baseTop, Math.min(prev.y, maxY)),
      }));
    };

    keepFloatingPanelInBounds();
    window.addEventListener("resize", keepFloatingPanelInBounds);
    return () => {
      window.removeEventListener("resize", keepFloatingPanelInBounds);
    };
  }, [isFormCollapsed, isMapVisible]);

  const resetFields = () => {
    setFrom(EMPTY_KATO);
    setTo(EMPTY_KATO);
    setCargoValue(null);
    setNumPack(null);
    setWeight(null);
    setProductName("");
    setProductCategory(EMPTY_PRODUCT_CATEGORY);
    setParcelPickup({});
    setParcelDelivery({});
    setPaidWeight(null);
    setSelectedOrg(EMPTY_ORG);
    setShowMessage(false);
    setExtraServices([]);
    setSearchMarkers([]);
    setIsFormCollapsed(false);
    setIsMapVisible(true);
    resetCalculationState();
    setHoveredSegmentIndex(null);
    setIsSegmentSidebarVisible(true);
    setSegmentSidebarOffset({ x: 0, y: 0 });
    setIsSegmentSidebarDragging(false);
    setFormContainerOffset({ x: 0, y: 0 });
    setIsCollapsedTriggerDragging(false);
    setIsFloatingPanelDragging(false);

    if (typeof window !== "undefined") {
      window.localStorage.removeItem(CALCULATOR_SPLIT_STORAGE_KEY);
    }
  };

  const handleCalculatorSubmit = (event) => {
    const accepted = sendCalculatorForm(event, requiredFields);
    setShowMessage(!accepted);
    if (accepted) {
      setIsCustomCarrierOptionsLoading(true);
      agent.Carriers.all(selectedOrg?.apiKey)
        .then((response) => setCustomCarrierOptions(normalizeCarrierOptions(response)))
        .catch(() => setCustomCarrierOptions([]))
        .finally(() => setIsCustomCarrierOptionsLoading(false));
    }
    return accepted;
  };

  const copyCalculatorDebugJson = () => {
    copyDebugJson(debugJson);
  };

  const onChangeFrom = (value) => {
    return agent.Kato.search(value).then(response => setFromOptions(response));
  };

  const onChangeTo = (value) => {
    return agent.Kato.search(value).then(response => setToOptions(response));
  };

  const onChangeProductCategories = (value) => {
    return agent.ProductCategory.all(value, "", 0, 50).then(response => {
      setProductCategoryOptions(response.content);
    });
  };

  const handleExtraServices = (e) => {
    const selected = [...extraServices];
    if (e.checked) {
      selected.push(e.value);
    } else {
      selected.splice(selected.indexOf(e.value), 1);
    }
    setExtraServices(selected);
  };

  const selectRoute = (route) => {
    setSelectedRoute(route);
    setSelectedRow(route);
  };

  const showOnMap = (route) => {
    setSelectedRow(route);
    setIsMapVisible(true);
    setIsFormCollapsed(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const radio = selectedRoute ? {
    psName: selectedRoute.vectors[0]?.carrierType || "",
    serviceType: selectedRoute.vectors[0]?.carrierType || "",
    postal: selectedRoute.vectors[0]?.psId || "",
  } : { psName: "", serviceType: "", postal: "" };
  const selectedRouteIdentity = getRouteIdentityKey(selectedRoute);
  const errorBannerIconClass = calcError?.statusCode === 502 ? "pi pi-refresh pi-spin" : "pi pi-bolt";

  const handleSegmentSidebarDragStart = (event) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    sidebarDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: segmentSidebarOffset.x,
      originY: segmentSidebarOffset.y,
    };
    setIsSegmentSidebarDragging(true);
  };

  const handleCollapsedTriggerDragStart = (event) => {
    if (!isMapVisible) {
      return;
    }
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    collapsedTriggerDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: formContainerOffset.x,
      originY: formContainerOffset.y,
    };
    setIsCollapsedTriggerDragging(true);
  };

  const handleCollapsedTriggerClick = () => {
    setIsFormCollapsed(false);
  };

  const handleFloatingPanelDragStart = (event) => {
    if (!isMapVisible || isFormCollapsed) {
      return;
    }
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    floatingPanelDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: formContainerOffset.x,
      originY: formContainerOffset.y,
    };
    setIsFloatingPanelDragging(true);
  };

  const handleCustomFilterChange = (field, value) => {
    setCustomRouteFilter((prev) => ({
      ...prev,
      [field]: value ?? null,
    }));
  };

  const handleCustomFilterSubmit = (event) => {
    event.preventDefault();

    const payload = {
      daysCntTo: customRouteFilter.maxDaysCnt,
      priceTo: customRouteFilter.maxPrice,
      carriers: customRouteFilter.carriers,
    };

    loadAdditionalRoutesPage(0, customPaginatorRows, payload);
  };

  const handleCustomFilterClear = () => {
    const emptyFilter = createEmptyCustomRouteFilter();
    setCustomRouteFilter(emptyFilter);
    loadAdditionalRoutesPage(0, customPaginatorRows, {
      daysCntTo: emptyFilter.maxDaysCnt,
      priceTo: emptyFilter.maxPrice,
      carriers: emptyFilter.carriers,
    });
  };

  return (
    <div className={styles.container}>
      {loading && (
        <div className={styles.searchOverlay}>
          <div className={styles.overlayCard}>
            <div className={styles.overlayAnimation}>
              <div className={styles.routeTrack}>
                <span />
                <span />
                <span />
              </div>
              <div className={styles.routeDot} />
              <div className={styles.routeTruck}><i className="pi pi-truck" /></div>
            </div>

            <h3 className={styles.overlayTitle}>Чуток магии логистики</h3>
            <p className={styles.overlayText}>
              Смотрим цену, скорость и комбинируем маршруты. Обычно это занимает пару секунд.
            </p>
          </div>
        </div>
      )}

      <section ref={heroSectionRef} className={`${styles.heroSection} ${!isMapVisible ? styles.heroSectionHidden : ""}`}>
        <Button
          type="button"
          icon={isMapVisible ? "pi pi-eye-slash" : "pi pi-eye"}
          label={isMapVisible ? "Скрыть карту" : "Показать карту"}
          className={`p-button-sm ${styles.mapToggleButton} ${!isMapVisible ? styles.mapToggleButtonStatic : ""}`}
          onClick={() => setIsMapVisible((prev) => !prev)}
        />
        {isMapVisible && (
          <>
            <RouteMap
              paths={selectedRow?.paths || []}
              vectors={selectedRow?.vectors || []}
              airExpressCodes={AIR_EXPRESS_CODES}
              segmentColors={MAP_SEGMENT_COLORS}
              hoveredSegmentIndex={hoveredSegmentIndex}
              onSegmentHoverChange={setHoveredSegmentIndex}
              searchMarkers={searchMarkers}
              className={styles.heroMap}
              mapStyle={{ height: "100%", width: "100%" }}
            />
            <div className={styles.mapShade} />
            {mapSegments.length > 0 && isSegmentSidebarVisible && (
              <aside
                className={`${styles.mapSegmentSidebar} ${isSegmentSidebarDragging ? styles.mapSegmentSidebarDragging : ""}`}
                style={{ transform: `translate(${segmentSidebarOffset.x}px, ${segmentSidebarOffset.y}px)` }}
              >
                <div className={styles.mapSegmentSidebarHead} onMouseDown={handleSegmentSidebarDragStart}>
                  <strong>Сегменты маршрута</strong>
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => setIsSegmentSidebarVisible(false)}
                    onMouseDown={(event) => event.stopPropagation()}
                    aria-label="Скрыть панель сегментов"
                  />
                </div>
                <div className={styles.mapSegmentList}>
                  {mapSegments.map((segment) => (
                    <div
                      key={`segment_meta_${segment.index}`}
                      className={`${styles.mapSegmentItem} ${hoveredSegmentIndex === segment.index ? styles.mapSegmentItemActive : ""}`}
                      onMouseEnter={() => setHoveredSegmentIndex(segment.index)}
                      onMouseLeave={() => setHoveredSegmentIndex(null)}
                    >
                      <span className={styles.mapSegmentColor} style={{ background: segment.color }} />
                      <div className={styles.mapSegmentMeta}>
                        <div className={styles.mapSegmentTop}>
                          <strong>Сегмент {segment.index + 1}</strong>
                          <span className={styles.mapSegmentBadge}>
                            {segment.mode === "air" ? "Авиа" : "Авто"}
                          </span>
                        </div>
                        <div className={styles.mapSegmentPath}>
                          {segment.fromName} ({segment.fromKato || "—"}) → {segment.toName} ({segment.toKato || "—"})
                        </div>
                        <div
                          className={`${styles.mapSegmentVectorType} map-segment-vector-type-tooltip`}
                          data-pr-tooltip={segment.vectorTypeTooltip}
                          data-pr-position="top"
                          title={segment.vectorTypeTooltip}
                          aria-label={segment.vectorTypeTooltip}
                        >
                          <i className={segment.vectorTypeIconClass} />
                          <span>{segment.vectorTypeNameRu}</span>
                        </div>
                        <div className={styles.mapSegmentExtra}>
                          <span>Услуга: {segment.carrierLabel}</span>
                          <span>{segment.distanceKm > 0 ? `${segment.distanceKm} км` : "—"}</span>
                        </div>
                        <div className={styles.mapSegmentMetrics}>
                          <div className={styles.mapSegmentMetric}>
                            <span>Срок</span>
                            <strong>{formatDayRangeOrDash(segment.minDay, segment.maxDay)}</strong>
                          </div>
                          <div className={styles.mapSegmentMetric}>
                            <span>Цена</span>
                            <strong>{formatMoneyOrDash(segment.price)}</strong>
                          </div>
                          <div className={styles.mapSegmentMetric}>
                            <span>Итого (цена с маржой)</span>
                            <strong>{formatMoneyOrDash(segmentTotalPrice)}</strong>
                          </div>
                          {hasMoneyValue(segment.przPrice) && (
                            <div className={styles.mapSegmentMetric}>
                              <span>ПРЗ</span>
                              <strong>{formatMoneyOrDash(segment.przPrice)}</strong>
                            </div>
                          )}
                          {hasMoneyValue(segment.prdPrice) && (
                            <div className={styles.mapSegmentMetric}>
                              <span>ПРД</span>
                              <strong>{formatMoneyOrDash(segment.prdPrice)}</strong>
                            </div>
                          )}
                          {hasMoneyValue(segment.crossDockingPrice) && (
                            <div className={styles.mapSegmentMetric}>
                              <span>Кросс-докинг</span>
                              <strong>{formatMoneyOrDash(segment.crossDockingPrice)}</strong>
                            </div>
                          )}
                          {segment.details.map((detail) => (
                            <div key={`${segment.index}_${detail.key}`} className={styles.mapSegmentMetric}>
                              <span>{detail.label}</span>
                              <strong>
                                {detail.kind === "money"
                                  ? formatMoneyOrDash(detail.value)
                                  : detail.value}
                              </strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={styles.mapSegmentTotal}>
                  <span>Итого (цена с маржой) по сегментам</span>
                  <strong>{formatMoneyOrDash(segmentTotalPrice)}</strong>
                </div>
              </aside>
            )}
            {mapSegments.length > 0 && !isSegmentSidebarVisible && (
              <Button
                type="button"
                icon="pi pi-list"
                label="Сегменты"
                className={`p-button-sm ${styles.mapSegmentSidebarToggle}`}
                onClick={() => setIsSegmentSidebarVisible(true)}
              />
            )}
          </>
        )}

        {!isFormCollapsed && (
          <div
            ref={floatingPanelRef}
            className={`${styles.floatingPanel} ${!isMapVisible ? styles.floatingPanelStatic : ""}`}
            style={isMapVisible ? { transform: `translate(${formContainerOffset.x}px, ${formContainerOffset.y}px)` } : undefined}
          >
            <div className={styles.panelHeader}>
              <div>
                {isMapVisible && (
                  <div
                    className={`${styles.panelDragHandle} ${isFloatingPanelDragging ? styles.panelDragHandleDragging : ""}`}
                    onPointerDown={handleFloatingPanelDragStart}
                    aria-label="Перетащить панель формы"
                  >
                    <i className="pi pi-arrows-alt" />
                    <span>Удерживайте здесь, чтобы переместить</span>
                  </div>
                )}
                <h2 className={styles.title}>Калькулятор (разделённый по плечам)</h2>
                <p className={styles.subtitle}>Выберите маршрут и параметры отправки, чтобы получить точный расчет.</p>
              </div>
              <Button
                type="button"
                icon="pi pi-window-minimize"
                className="p-button-rounded p-button-text"
                onClick={() => setIsFormCollapsed(true)}
                onPointerDown={(event) => event.stopPropagation()}
                aria-label="Свернуть форму"
              />
            </div>

            {showMessage && <h4 className={styles.message}>Заполните все поля</h4>}

            <form id="calculatorShoulderForm" onSubmit={handleCalculatorSubmit} className={styles.form}>
              <div className={styles.grid2}>
                <div className={styles.where}>
                  <div className={styles.searchHeader}>
                    <span className={styles.searchLabel}>Откуда</span>
                    <span className={styles.searchHint}>Введите город или населенный пункт (минимум 3 символа)</span>
                  </div>
                  <SearchSelect
                    placeholder="Начните вводить пункт отправления..."
                    name="from"
                    state={from}
                    setState={setFrom}
                    options={fromOptions}
                    optionValue={"kato"}
                    onChange={onChangeFrom}
                    optionText={"nameRu"}
                    highlightRequired={showMessage && !from.kato}
                  />
                  <span className={styles.kato}>
                    <span>Като: {from.kato || "-"}</span>
                    <span className={styles.katoFullName}>Локация: {getLocationFullName(from)}</span>
                  </span>
                </div>

                <div className={styles.where}>
                  <div className={styles.searchHeader}>
                    <span className={styles.searchLabel}>Куда</span>
                    <span className={styles.searchHint}>Введите город или населенный пункт (минимум 3 символа)</span>
                  </div>
                  <SearchSelect
                    placeholder="Начните вводить пункт назначения..."
                    name="to"
                    state={to}
                    setState={setTo}
                    options={toOptions}
                    optionValue={"kato"}
                    onChange={onChangeTo}
                    optionText={"nameRu"}
                    highlightRequired={showMessage && !to.kato}
                  />
                  <span className={styles.kato}>
                    <span>Като: {to.kato || "-"}</span>
                    <span className={styles.katoFullName}>Локация: {getLocationFullName(to)}</span>
                  </span>
                </div>
              </div>

              <div className={styles.grid2}>
                <div className={styles.where}>
                  <div className={styles.searchHeader}>
                    <span className={styles.searchLabel}>Категория товара</span>
                    <span className={styles.searchHint}>Введите название категории (минимум 3 символа)</span>
                  </div>
                  <SearchSelect
                    placeholder="Начните вводить категорию товара..."
                    name="productCategory"
                    state={productCategory}
                    setState={setProductCategory}
                    options={productCategoryOptions}
                    optionValue={"code"}
                    onChange={onChangeProductCategories}
                    optionText={"name_ru"}
                    highlightRequired={showMessage && !productCategory.code}
                  />
                </div>

                <div className={styles.pickup}>
                  <SearchSelect
                    placeholder="Забор"
                    name="parcelPickup"
                    state={parcelPickup}
                    setState={setParcelPickup}
                    options={parcelPickupOptions}
                    optionValue={"code"}
                    onChange={NOOP}
                    optionText={"name_ru"}
                    highlightRequired={showMessage && !parcelPickup?.code}
                    isSearchable={false}
                    enableRemoteSearch={false}
                  />
                  <SearchSelect
                    placeholder="Доставка"
                    name="parcelDelivery"
                    state={parcelDelivery}
                    setState={setParcelDelivery}
                    options={parcelDeliveryOptions}
                    optionValue={"code"}
                    onChange={NOOP}
                    optionText={"name_ru"}
                    highlightRequired={showMessage && !parcelDelivery?.code}
                    isSearchable={false}
                    enableRemoteSearch={false}
                  />
                </div>
              </div>

              <div className={styles.inputs}>
                <SearchSelect
                  placeholder="Выберите ключ клиента"
                  name="calcKey"
                  state={selectedOrg}
                  setState={setSelectedOrg}
                  options={props?.orgs || []}
                  optionValue={"id"}
                  onChange={NOOP}
                  optionText={"name"}
                  highlightRequired={showMessage && !selectedOrg?.id}
                  enableRemoteSearch={false}
                />

                <div className={styles.input}>
                  <InputNumber
                    placeholder="Количество мест"
                    name="numPack"
                    value={numPack}
                    onValueChange={(e) => setNumPack(e.value)}
                    inputStyle={{ borderColor: showMessage && !numPack ? "red" : "#ccc" }}
                  />
                </div>

                <div className={styles.input}>
                  <InputNumber
                    placeholder="Платный вес, кг"
                    name="paidWeight"
                    value={paidWeight}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    onValueChange={(e) => setPaidWeight(e.value)}
                    inputStyle={{ borderColor: showMessage && !paidWeight ? "red" : "#ccc" }}
                  />
                </div>

                <div className={styles.input}>
                  <InputNumber
                    placeholder="Физ вес, кг"
                    name="weight"
                    value={weight}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    onValueChange={(e) => setWeight(e.value)}
                    inputStyle={{ borderColor: showMessage && !weight ? "red" : "#ccc" }}
                  />
                </div>

                <div className={styles.input}>
                  <InputText
                    placeholder="Наименование товара"
                    name="productName"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    style={{ borderColor: showMessage && !productName ? "red" : "#ccc" }}
                  />
                </div>

                <div className={styles.input}>
                  <InputNumber
                    placeholder="Стоимость товара"
                    name="cargoValue"
                    value={cargoValue}
                    onValueChange={(e) => setCargoValue(e.value)}
                    inputStyle={{ borderColor: showMessage && !cargoValue ? "red" : "#ccc" }}
                  />
                </div>
              </div>

              <div className={styles.checkboxes}>
                <div
                  className={`p-field-checkbox ${styles.checkboxItem}`}
                  data-pr-tooltip="Включить в стоимость оплату за разгрузку товара грузчиками"
                >
                  <Checkbox
                    inputId={ESPRD}
                    name="service"
                    value={ESPRD}
                    onChange={handleExtraServices}
                    checked={extraServices.indexOf(ESPRD) !== -1}
                  />
                  <label htmlFor={ESPRD}>Услуга разгрузки при получении</label>
                </div>

                <div
                  className={`p-field-checkbox ${styles.checkboxItem}`}
                  data-pr-tooltip="Включить в стоимость оплату за погрузку товара грузчиками"
                >
                  <Checkbox
                    inputId={ESPRZ}
                    name="service"
                    value={ESPRZ}
                    onChange={handleExtraServices}
                    checked={extraServices.indexOf(ESPRZ) !== -1}
                  />
                  <label htmlFor={ESPRZ}>Услуга погрузки при отправлении</label>
                </div>
              </div>

              <div className={styles.buttons}>
                <div className={styles.button}>
                  <Button type="reset" onClick={resetFields} label="Сбросить" className="p-button-warning" />
                </div>
                <div className={styles.button}>
                  <Button loading={loading} type="submit" label="Рассчитать" />
                </div>
              </div>
            </form>
          </div>
        )}

        {isFormCollapsed && (
          <button
            type="button"
            ref={collapsedTriggerRef}
            className={`${styles.collapsedTrigger} ${!isMapVisible ? styles.collapsedTriggerStatic : ""} ${isCollapsedTriggerDragging ? styles.collapsedTriggerDragging : ""}`}
            onClick={handleCollapsedTriggerClick}
            style={isMapVisible ? { transform: `translate(${formContainerOffset.x}px, ${formContainerOffset.y}px)` } : undefined}
            aria-label="Открыть форму калькулятора"
          >
            {isMapVisible && (
              <span
                className={styles.collapsedDragHandle}
                onPointerDown={handleCollapsedTriggerDragStart}
                onClick={(event) => event.stopPropagation()}
              >
                <i className="pi pi-arrows-alt" />
                <span>Удерживайте здесь, чтобы переместить</span>
              </span>
            )}
            <img src={mapPreviewImage} className={styles.collapsedImage} alt="" />
            <span className={styles.collapsedMeta}>
              <i className="pi pi-compass" />
              <span>Открыть форму расчета</span>
            </span>
          </button>
        )}
      </section>

      {shouldShowResultsSection && (
        <section ref={tableRef} className={styles.resultsSection}>
          <div className={styles.resultHeader}>
            <div>
              <h2 className={styles.resultTitle}>Выбор тарифа</h2>
              <p className={styles.resultHint}>Ключевые тарифные стратегии и отдельный блок «Индивидуальный» для остальных маршрутов.</p>
            </div>
            {selectedRoute && (
              <div className={styles.selectedBadge}>
                <i className="pi pi-check-circle" />
                Выбран маршрут №{selectedRoute?.num ?? selectedRoute?.pathId?.slice(0, 8) ?? "—"}
              </div>
            )}
          </div>

          <div className={styles.mixGrid}>
          {highlightedRoutes.map((item) => {
              const hasRoute = Boolean(item.route);
              const isOptimalRouteLoading = item.key === "MIX_OPT" && isAdditionalPolling && !hasRoute;
              return (
                <article
                  key={`${item.key}_${item.route?.num ?? item.route?.pathId ?? "empty"}`}
                  className={`${styles.mixCard} ${styles[`mixCard_${item.key}`]} ${!hasRoute ? styles.mixCardEmpty : ""} ${selectedRouteIdentity && selectedRouteIdentity === getRouteIdentityKey(item.route) ? styles.mixCardSelected : ""}`}
                >
                  <div className={styles.mixHead}>
                    <span className={styles.mixTitle}>{item.title}</span>
                    <div className={styles.mixBadges}>
                      {hasRoute && isAirExpressRoute(item.route) && (
                        <span className={styles.airExpressBadge}>
                          <img src={AIRPLANE_BADGE_SRC} alt="" />
                          Авиа
                        </span>
                      )}
                      <span className={styles.mixBadge}>{item.badge}</span>
                    </div>
                  </div>

                  <div className={styles.mixTopContent}>
                    <p className={styles.mixSubtitle}>{item.subtitle}</p>
                    {isOptimalRouteLoading && (
                      <p className={styles.mixLoadingNote}>
                        <i className={`pi pi-spin pi-clock ${styles.mixLoadingIcon}`} aria-hidden="true" />
                        Ищем лучший вариант для тарифa «Тариф Оптимальный»
                      </p>
                    )}

                    {!hasRoute && !isOptimalRouteLoading && (
                      <p className={styles.mixEmptyNote}>
                        {item.emptyText || "Сервис расчета не вернул маршрут для этого тарифа."}
                      </p>
                    )}

                    <div className={styles.mixMeta}>
                      <div>
                        <span className={styles.metaLabel}>Маршрут</span>
                        <span className={styles.metaValue}>{getRoutePath(item.route)}</span>
                      </div>
                      <div>
                        <span className={styles.metaLabel}>Перевозчики</span>
                        <span className={styles.metaValue}>{getRouteCarriers(item.route)}</span>
                      </div>
                    </div>

                    <div className={styles.mixStats}>
                      <div>
                        <span className={styles.statLabel}>Цена</span>
                        <strong>{hasRoute ? formatPrice(item.route.price) : "—"}</strong>
                      </div>
                      <div>
                        <span className={styles.statLabel}>Итого (цена с маржой)</span>
                        <strong>{hasRoute ? formatPrice(getRouteVectorTotalPrice(item.route)) : "—"}</strong>
                      </div>
                      <div>
                        <span className={styles.statLabel}>Срок</span>
                        <strong>{hasRoute ? getRouteDaysLabel(item.route) : "—"}</strong>
                      </div>
                      <div>
                        <span className={styles.statLabel}>Расстояние</span>
                        <strong>{hasRoute ? `${getDistanceKm(item.route)} км` : "—"}</strong>
                      </div>
                    </div>
                  </div>

                  <div className={styles.mixActions}>
                    <Button
                      type="button"
                      icon="pi pi-map"
                      label="На карте"
                      className="p-button-outlined p-button-sm"
                      onClick={() => hasRoute && showOnMap(item.route)}
                      disabled={!hasRoute}
                    />
                    <Button
                      type="button"
                      icon="pi pi-check"
                      label="Выбрать"
                      className="p-button-sm"
                      onClick={() => hasRoute && selectRoute(item.route)}
                      disabled={!hasRoute}
                    />
                  </div>
                </article>
              );
            })}
          </div>

          <Accordion className={styles.customAccordion}>
            <AccordionTab
              header={(
                <div className={styles.customHeader}>
                  <span>Тариф Индивидуальный</span>
                  <div className={styles.customHeaderMeta}>
                    {isCustomPolling && <i className={`pi pi-sync pi-spin ${styles.customLoadingIcon}`} aria-hidden="true" />}
                    {isAdditionalPolling && <i className={`pi pi-spin pi-clock ${styles.customLoadingIcon}`} aria-hidden="true" />}
                    {hasAdditionalPagination && (
                      <span className={styles.customPageMeta}>
                        {additionalPagination.totalPages > 0 ? `${additionalPagination.pageNumber + 1}/${additionalPagination.totalPages}` : "0/0"}
                      </span>
                    )}
                    <span className={styles.customCount}>{customTotalRecords}</span>
                  </div>
                </div>
              )}
            >
              <form className={styles.customFilterCard} onSubmit={handleCustomFilterSubmit}>
                <div className={styles.customFilterHeader}>
                  <div>
                    <h4>Фильтр маршрутов</h4>
                  </div>
                </div>

                <div className={styles.customFilterGrid}>
                  <div className={styles.customFilterField}>
                    <label htmlFor="customFilterMaxDays">Макс. срок</label>
                    <InputNumber
                      inputId="customFilterMaxDays"
                      value={customRouteFilter.maxDaysCnt}
                      onValueChange={(event) => handleCustomFilterChange("maxDaysCnt", event.value)}
                      placeholder="Например, 10"
                      useGrouping={false}
                      min={0}
                    />
                  </div>
                  <div className={styles.customFilterField}>
                    <label htmlFor="customFilterMaxPrice">Макс. цена</label>
                    <InputNumber
                      inputId="customFilterMaxPrice"
                      value={customRouteFilter.maxPrice}
                      onValueChange={(event) => handleCustomFilterChange("maxPrice", event.value)}
                      placeholder="Например, 120000"
                      min={0}
                    />
                  </div>
                  <div className={styles.customFilterField}>
                    <label htmlFor="customFilterCarriers">Грузоперевозчики</label>
                    <MultiSelect
                      inputId="customFilterCarriers"
                      value={customRouteFilter.carriers}
                      onChange={(event) => handleCustomFilterChange("carriers", event.value || [])}
                      options={customCarrierOptions}
                      optionValue="code"
                      optionLabel="nameRu"
                      placeholder={isCustomCarrierOptionsLoading ? "Загружаем..." : "Выберите перевозчиков"}
                      display="chip"
                      filter
                      disabled={isCustomCarrierOptionsLoading}
                      className={styles.customFilterMultiSelect}
                    />
                  </div>
                </div>

                <div className={styles.customFilterActions}>
                  <Button type="button" label="Очистить" className="p-button-outlined p-button-sm" onClick={handleCustomFilterClear} />
                  <Button type="submit" icon="pi pi-send" label="Применить" className="p-button-sm" />
                </div>
              </form>

              {isAnyCustomPolling && (
                <div className={styles.customLoadingBanner}>
                  <div>
                    <strong>
                      {isAdditionalPolling
                        ? "Индивидуальные маршруты догружаются постранично"
                        : "Индивидуальные маршруты в процессе расчёта"}
                    </strong>
                    <p>
                      {isAdditionalPolling
                        ? "Обновляем страницу маршрутов. Новые данные появятся автоматически."
                        : "Основные тарифы уже готовы. Остальные появятся через несколько секунд."}
                    </p>
                  </div>
                </div>
              )}

              {customPollingError && !isAnyCustomPolling && (
                <div className={styles.customLoadingError}>
                  <i className="pi pi-exclamation-triangle" />
                  <span>{customPollingError}</span>
                </div>
              )}

              {customTotalRecords === 0 && !isAnyCustomPolling && !customPollingError && (
                <div className={styles.customEmpty}>Все маршруты уже вошли в основные тарифы.</div>
              )}

              {visibleCustomRoutes.length > 0 && (
                <>
                  <div className={styles.customList}>
                    {visibleCustomRoutes.map((route, routeIndex) => {
                      const routeIdentity = getRouteIdentityKey(route);
                      const routeLabel = route?.num ?? route?.pathId?.slice(0, 8) ?? (routeIndex + 1);

                      return (
                        <article
                          key={`custom_${routeIdentity || routeLabel}`}
                          className={`${styles.customItem} ${selectedRouteIdentity && selectedRouteIdentity === routeIdentity ? styles.customItemSelected : ""}`}
                        >
                          <div className={styles.customMain}>
                            <div className={styles.customNum}>№{routeLabel}</div>
                            <div className={styles.customTexts}>
                              <p className={styles.customPath}>{getRoutePath(route)}</p>
                              <p className={styles.customCarriers}>{getRouteCarriers(route)}</p>
                            </div>
                            {isAirExpressRoute(route) && (
                              <div className={styles.customExpressZone}>
                                <div className={styles.customExpressPlane}>
                                  <img src={AIRPLANE_BADGE_SRC} alt="" />
                                </div>
                                <span className={styles.customExpressLabel}>Авиа</span>
                              </div>
                            )}
                          </div>

                          <div className={styles.customStats}>
                            <span>Цена: {formatPrice(route.price)}</span>
                            <span>Итого (цена с маржой): {formatPrice(getRouteVectorTotalPrice(route))}</span>
                            <span>{getRouteDaysLabel(route)}</span>
                            <span>{getDistanceKm(route)} км</span>
                          </div>

                          <div className={styles.customActions}>
                            <Button
                              type="button"
                              icon="pi pi-map"
                              className="p-button-rounded p-button-text"
                              onClick={() => showOnMap(route)}
                              aria-label="Показать на карте"
                            />
                            <Button
                              type="button"
                              icon="pi pi-check"
                              className="p-button-rounded p-button-text"
                              onClick={() => selectRoute(route)}
                              aria-label="Выбрать маршрут"
                            />
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  <Paginator
                    className={styles.customPaginator}
                    first={customPaginatorFirst}
                    rows={customPaginatorRows}
                    totalRecords={customTotalRecords}
                    onPageChange={(e) => {
                      if (hasAdditionalPagination) {
                        loadAdditionalRoutesPage(e.page, e.rows);
                        return;
                      }
                      setCustomFirst(e.first);
                    }}
                    template="PrevPageLink CurrentPageReport NextPageLink"
                    currentPageReportTemplate="{first} - {last} из {totalRecords}"
                  />
                </>
              )}
            </AccordionTab>
          </Accordion>
        </section>
      )}

      {hasCalculated && !calcError && result.length === 0 && !loading && !isAnyCustomPolling && !shouldShowResultsSection && (
        <section ref={tableRef} className={styles.emptyStateSection}>
          <div className={styles.emptyStateCard}>
            <div className={styles.emptyStateEmoji} aria-hidden="true">
              <i className="pi pi-compass" />
            </div>
            <h2 className={styles.emptyStateTitle}>Маршруты пока не нашли путь</h2>
            <p className={styles.emptyStateText}>
              Похоже, логистический компас задумался. Попробуйте слегка изменить параметры, и найдём вариант быстрее.
            </p>

            <div className={styles.emptyStateHints}>
              <span>Проверьте пункты отправки и назначения</span>
              <span>Измените вес или количество мест</span>
              <span>Попробуйте без доп. услуг</span>
            </div>

            <div className={styles.emptyStateActions}>
              <Button
                type="button"
                icon="pi pi-sliders-h"
                label="Подправить параметры"
                onClick={() => {
                  setIsFormCollapsed(false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
              <Button
                type="button"
                icon="pi pi-refresh"
                label="Сбросить и начать заново"
                className="p-button-outlined p-button-secondary"
                onClick={resetFields}
              />
            </div>

            {debugJson && (
              <div className={styles.debugPayloadCard}>
                <div className={styles.debugPayloadHead}>
                  <span>Данные для разработчиков</span>
                  <Button
                    type="button"
                    icon={isDebugCopied ? "pi pi-check" : "pi pi-copy"}
                    label={isDebugCopied ? "Скопировано" : "Скопировать JSON"}
                    className="p-button-sm p-button-text"
                    onClick={copyCalculatorDebugJson}
                  />
                </div>
                <pre className={styles.debugPayloadCode}>{debugJson}</pre>
              </div>
            )}
          </div>
        </section>
      )}

      {hasCalculated && calcError && !loading && (
        <section ref={tableRef} className={styles.emptyStateSection}>
          <div className={`${styles.emptyStateCard} ${styles.emptyStateErrorCard}`}>
            {calcError.bannerText && (
              <div className={styles.emptyStateErrorBanner}>
                <i className={errorBannerIconClass} />
                <span>{calcError.bannerText}</span>
              </div>
            )}

            <h2 className={styles.emptyStateTitle}>{calcError.title}</h2>
            <p className={styles.emptyStateText}>{calcError.text}</p>
            {calcError.statusCode && (
              <p className={styles.emptyStateCode}>Код ошибки: {calcError.statusCode}</p>
            )}

            <div className={styles.emptyStateActions}>
              <Button
                type="button"
                icon="pi pi-refresh"
                label="Попробовать снова"
                onClick={(e) => handleCalculatorSubmit(e)}
              />
              <Button
                type="button"
                icon="pi pi-sliders-h"
                label="Изменить параметры"
                className="p-button-outlined p-button-secondary"
                onClick={() => {
                  setIsFormCollapsed(false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </div>

            {debugJson && (
              <div className={styles.debugPayloadCard}>
                <div className={styles.debugPayloadHead}>
                  <span>Данные для разработчиков</span>
                  <Button
                    type="button"
                    icon={isDebugCopied ? "pi pi-check" : "pi pi-copy"}
                    label={isDebugCopied ? "Скопировано" : "Скопировать JSON"}
                    className="p-button-sm p-button-text"
                    onClick={copyCalculatorDebugJson}
                  />
                </div>
                <pre className={styles.debugPayloadCode}>{debugJson}</pre>
              </div>
            )}
          </div>
        </section>
      )}

      {!calcError && result.length > 0 && (
        <section className={styles.orderSection}>
          <Form
            result={result || []}
            from={from}
            to={to}
            cargoValue={cargoValue}
            numPack={numPack}
            weight={weight}
            productName={productName}
            productCategory={productCategory}
            paidWeight={paidWeight}
            radio={radio}
            orgId={selectedOrg.id}
            selectedOrg={selectedOrg}
            selectedRoute={selectedRoute}
            hashCodeRequest={calculationHashCodeRequest}
            buildPathRequestId={calculationBuildPathRequestId}
            routeId={calculationSelectedPathId}
            parcelPickup={parcelPickup}
            parcelDelivery={parcelDelivery}
            onGeocodeMarkerChange={setSearchMarkers}
            canSubmitOrder={Boolean(selectedRoute)}
          />
        </section>
      )}

      <Tooltip target=".p-field-checkbox" />
      <Tooltip
        target=".map-segment-vector-type-tooltip"
        position="top"
        showDelay={120}
        hideDelay={70}
        autoZIndex
        baseZIndex={2147483000}
        className="segment-vector-type-tooltip-overlay"
      />
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CalculatorSplit);
